import { useEffect, useRef, forwardRef } from "react";
import gsap from "gsap";

interface TiltBoxProps extends React.HTMLAttributes<HTMLDivElement> {}

const TiltBox = forwardRef<HTMLDivElement, TiltBoxProps>(
  ({ className, style, ...props }, ref) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const rotateXTo = useRef<gsap.QuickToFunc | null>(null);
    const rotateYTo = useRef<gsap.QuickToFunc | null>(null);
    const rectRef = useRef<DOMRect | null>(null);

    useEffect(() => {
      if (!contentRef.current) return;

      rotateXTo.current = gsap.quickTo(contentRef.current, "rotationX", {
        duration: 0.5,
        ease: "power3.out",
      });
      rotateYTo.current = gsap.quickTo(contentRef.current, "rotationY", {
        duration: 0.5,
        ease: "power3.out",
      });

      gsap.set(contentRef.current, {
        transformPerspective: 800,
        transformOrigin: "center center",
        willChange: "transform",
      });
    }, []);

    const handleMouseEnter = () => {
      if (!contentRef.current) return;
      rectRef.current = contentRef.current.getBoundingClientRect();
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!rectRef.current || !rotateXTo.current || !rotateYTo.current) return;

      const rect = rectRef.current;

      const nx = gsap.utils.clamp(0.05, 0.95, (e.clientX - rect.left) / rect.width);
      const ny = gsap.utils.clamp(0.05, 0.95, (e.clientY - rect.top) / rect.height);

      const referenceWidth = 400;
      const sizeFactor = Math.sqrt(referenceWidth / Math.max(rect.width, 20));
      const maxRotation = Math.min(50 * sizeFactor, 25);

      const rotateX = gsap.utils.interpolate(maxRotation, -maxRotation, ny);
      const rotateY = gsap.utils.interpolate(-maxRotation, maxRotation, nx);

      rotateXTo.current(rotateX);
      rotateYTo.current(rotateY);
    };

    const handleMouseLeave = () => {
      rectRef.current = null;
      rotateXTo.current?.(0);
      rotateYTo.current?.(0);
    };

    return (
      <div
        ref={ref}
        className={className}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <div ref={contentRef} className="w-full h-full bg-black" />
      </div>
    );
  }
);

TiltBox.displayName = "TiltBox";
export default TiltBox;
